"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { registerAppDialog } from "../lib/appDialog";

const AppDialogContext = createContext(null);

export function AppDialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);

  const close = useCallback(() => setDialog(null), []);

  const alert = useCallback((message, title = "Notice") => {
    return new Promise((resolve) => {
      setDialog({
        title,
        message: String(message ?? ""),
        alertOnly: true,
        confirmLabel: "OK",
        onConfirm: () => {
          close();
          resolve();
        },
        onCancel: () => {
          close();
          resolve();
        },
      });
    });
  }, [close]);

  const confirm = useCallback(
    (message, title = "Confirm", options = {}) => {
      return new Promise((resolve) => {
        setDialog({
          title,
          message: String(message ?? ""),
          confirmLabel: options.confirmLabel ?? "Confirm",
          cancelLabel: options.cancelLabel ?? "Cancel",
          variant: options.variant ?? "default",
          alertOnly: false,
          onConfirm: () => {
            close();
            resolve(true);
          },
          onCancel: () => {
            close();
            resolve(false);
          },
        });
      });
    },
    [close]
  );

  useEffect(() => {
    registerAppDialog({ alert, confirm });
    return () => registerAppDialog(null);
  }, [alert, confirm]);

  return (
    <AppDialogContext.Provider value={{ alert, confirm }}>
      {children}
      <ConfirmDialog
        open={!!dialog}
        title={dialog?.title ?? ""}
        message={dialog?.message ?? ""}
        confirmLabel={dialog?.confirmLabel}
        cancelLabel={dialog?.cancelLabel}
        alertOnly={dialog?.alertOnly}
        variant={dialog?.variant}
        onConfirm={dialog?.onConfirm ?? close}
        onCancel={dialog?.onCancel ?? close}
      />
    </AppDialogContext.Provider>
  );
}

export function useAppDialog() {
  const ctx = useContext(AppDialogContext);
  if (!ctx) {
    throw new Error("useAppDialog must be used within AppDialogProvider");
  }
  return ctx;
}
